'use client'

import {
  EntityDetailSkeleton,
  AnalysisFindingsCards,
  MatchQualityBadge,
  ScoreRing,
  RequirementsCoverageSection,
  resolveAnalysisRequirements,
  summarizeRequirements,
  Button,
  Card,
  CardContent,
  EntityAboutSection,
  useEvaluationAnalysis,
  useRetryFitAnalysis,
  FIT_ANALYSIS_COPY,
} from '@yeshuman/sdk'
import { FileText } from 'lucide-react'
import { AnalysisRetryButton } from '../search/analysis-retry-button'
import type { Opportunity } from '../../hooks/use-opportunities'

export type CandidateFitDocumentProps = {
  evaluationId: string
  opportunity: Opportunity
  subjectLabel?: string
  onApply?: () => void
}

export function CandidateFitDocument({
  evaluationId,
  opportunity,
  subjectLabel,
  onApply,
}: CandidateFitDocumentProps) {
  const { payload, isLoading, isMissing, error } = useEvaluationAnalysis(evaluationId)
  const { retryCandidate, mutatingOpportunityId } = useRetryFitAnalysis()
  const retryPending = mutatingOpportunityId === opportunity.id
  const retryButton = (
    <AnalysisRetryButton
      mode={payload ? 'run' : 'try'}
      onClick={() => retryCandidate(opportunity.id)}
      pending={retryPending}
    />
  )

  if (isLoading) {
    return (
      <EntityDetailSkeleton
        variant="compact"
        withPageContainer={false}
        ariaLabel="Loading fit analysis"
      />
    )
  }

  if (error || isMissing || !payload) {
    return (
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-sm text-muted-foreground" role="status" aria-live="polite">
          {error instanceof Error ? error.message : FIT_ANALYSIS_COPY.missing}
        </p>
        {retryButton}
      </div>
    )
  }

  const matchScore = payload.scoring_trace.final_score ?? 0
  const { requirements } = resolveAnalysisRequirements(payload)
  const { metCount, knownCount } = summarizeRequirements(requirements)

  return (
    <div className="space-y-6">
      {onApply || retryButton ? (
        <div className="flex flex-wrap items-center gap-2">
          {onApply ? (
            <Button type="button" size="sm" onClick={onApply}>
              Apply
            </Button>
          ) : null}
          {retryButton}
        </div>
      ) : null}

      <Card>
        <CardContent className="grid gap-5 p-0 sm:grid-cols-[auto_1fr] sm:items-center">
          <ScoreRing score={matchScore} caption="match" className="mx-auto sm:mx-0" />
          <div className="space-y-2">
            {subjectLabel ? <p className="text-sm text-muted-foreground">{subjectLabel}</p> : null}
            <MatchQualityBadge score={matchScore} showPercent={false} />
            <h2 className="text-lg font-semibold">{payload.headline}</h2>
            {payload.narrative.summary ? (
              <p className="whitespace-pre-wrap text-sm text-muted-foreground">
                {payload.narrative.summary}
              </p>
            ) : null}
          </div>
        </CardContent>
      </Card>

      <AnalysisFindingsCards
        highlights={payload.findings.highlights}
        redFlags={payload.findings.red_flags}
        highlightsTitle="Why you fit"
        redFlagsTitle="Gaps"
        twoColumn
      />

      {requirements.length > 0 ? (
        <Card>
          <CardContent className="p-0">
            {knownCount > 0 ? (
              <p className="px-[var(--sp-card)] pt-[var(--sp-card)] text-xs text-muted-foreground">
                {metCount} of {knownCount} must-haves met
              </p>
            ) : null}
            <RequirementsCoverageSection requirements={requirements} />
          </CardContent>
        </Card>
      ) : null}

      <EntityAboutSection
        title="Job description"
        leadingIcon={FileText}
        body={opportunity.description}
        stripLeadingTitles={[opportunity.title]}
        emptyVariant="text"
        emptyMessage="No description provided."
      />
    </div>
  )
}
